import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { useIntakeStyle } from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import { INTAKE_UPLOADS } from '@/karehero-app/tell-us-once/intake/content';

// Something they handed over, in the transcript where they handed it over. Until
// now tapping an upload row put the words "Uploaded a document" in the chat,
// which said it had happened without showing it: the flow's whole argument is
// that a photo or a file is a way of answering, so what they sent has to be as
// visible as what they typed.
//
// Coral like the rest of their side of the conversation. A photo shows itself; a
// document cannot, so it says what it is instead.
//
// Three states:
//
// `uploading` is the file leaving the phone. Nothing can be done with it yet.
// `sent` is arrived. Nothing says it is being read: everything is read at the
//   end, once they say they are done (see ProcessingScreen).
// `failed` is it never arrived. Distinct from a file that arrived and could not
//   be read, which surfaces after the reading, not in the chat.
export type AttachmentKind = 'photo' | 'document';
// `processing` is kept for the family claim prototype, which still passes it. It
// draws the same as `sent`: nothing in the chat says a file is being read.
export type AttachmentState = 'uploading' | 'processing' | 'sent' | 'failed';

const PHOTO_PLACEHOLDER = '/vector/photo-placeholder.svg';

// Red when it never left the phone. FailedMessage puts the alert and the way to
// try again around it, the same as it does for a message.
const shell = (state: AttachmentState, reply: string) =>
  state === 'failed' ? 'error.main' : reply;

const Shell = ({
  kind,
  name,
  state,
}: {
  kind: AttachmentKind;
  name?: string;
  state: AttachmentState;
}) => {
  const { reply, replyInk } = useIntakeStyle();
  // Light on red when it failed; otherwise whatever reads on the reply colour.
  const ink = state === 'failed' ? 'custom.neutral.50' : replyInk;
  return kind === 'photo' ? (
    <Stack
      sx={{
        width: 180,
        p: 0.75,
        bgcolor: shell(state, reply),
        borderRadius: `${radius.lg}px`,
        borderBottomRightRadius: '4px',
      }}
      spacing={0.75}
    >
      <Box
        sx={{
          position: 'relative',
          height: 116,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          backgroundImage: `url(${PHOTO_PLACEHOLDER})`,
          backgroundSize: 'cover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'custom.neutral.500',
        }}
      >
        {state === 'uploading' ? (
          <CircularProgress size={28} sx={{ color: 'custom.coral.600' }} />
        ) : (
          /* A photo of a document, not the document: nothing here is a real ID,
             and drawing a convincing one would be a worse lie than a marker. */
          <Icon icon={'id-card' as never} fontSize={32} />
        )}
      </Box>
    </Stack>
  ) : (
    <Stack
      sx={{
        maxWidth: 260,
        px: 1.5,
        py: 1.25,
        bgcolor: shell(state, reply),
        borderRadius: `${radius.lg}px`,
        borderBottomRightRadius: '4px',
      }}
      spacing={1}
    >
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box sx={{ color: ink, display: 'flex', flexShrink: 0 }}>
          <Icon icon={'file-lines' as never} fontSize={20} />
        </Box>
        <Typography sx={{ color: ink, minWidth: 0 }} variant='body2'>
          {name}
        </Typography>
      </Stack>
      {state === 'uploading' && (
        <LinearProgress
          sx={{
            bgcolor: 'custom.coral.600',
            '& .MuiLinearProgress-bar': { bgcolor: ink },
          }}
        />
      )}
    </Stack>
  );
};

const Attachment = ({
  kind,
  name,
  state = 'sent',
}: {
  kind: AttachmentKind;
  /** What the file is called. Documents only: a photo is its own label. */
  name?: string;
  state?: AttachmentState;
}) => (
  // Aligned to the attachment's edge, which is their side of the conversation.
  <Stack sx={{ alignItems: 'flex-end' }} spacing={0.75}>
    <Shell kind={kind} name={name} state={state} />
  </Stack>
);

export default Attachment;
